import React from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import CircularProgress from 'components/shared/CircularProgress';
import DocumentLink from 'components/shared/DocumentLink';
import Checkbox from 'components/shared/form/Checkbox';
import Tooltip from 'components/shared/Tooltip';
import { SummarizableReview } from 'lib/aiJobHelpers';
import { dateAtTime } from 'lib/date';
import Attachment from 'models/Attachment';
import colors from 'styles/theme/colors';
import { BodySmallBold, Label } from 'styles/typography';
import ErrorWarningIcon from 'svg/ErrorWarningIcon';

const columnHelper = createColumnHelper<Attachment>();

type FilesColumnsProps = {
  additionalFilesSelection: Record<string, boolean>;
  setAdditionalFilesSelection: (additionalFilesSelection: Record<string, boolean>) => void;
  removeFile: (file: Attachment) => void;
  review: SummarizableReview;
};

export default function filesColumns({
  additionalFilesSelection,
  setAdditionalFilesSelection,
  removeFile,
  review,
}: FilesColumnsProps) {
  return [
    columnHelper.accessor((row) => row, {
      enableSorting: false,
      id: 'additionalFilesSelection',
      size: 0,
      meta: {
        padding: '0 0 0 16px',
        style: {
          flexGrow: '0',
          zIndex: '1000',
        },
      },
      header: () =>
        Object.values(additionalFilesSelection).some((value) => value) ? (
          <Checkbox
            size={12}
            checked={true}
            indeterminate={!Object.values(additionalFilesSelection).every((value) => value)}
            checkedColor={colors.primaryBlue}
            onChange={() => {
              setAdditionalFilesSelection({});
            }}
          />
        ) : (
          <></>
        ),
      cell: (info) => (
        <Checkbox
          size={12}
          checked={additionalFilesSelection[info.row.original.id]}
          checkedColor={colors.primaryBlue}
          onChange={(checked) => {
            setAdditionalFilesSelection({ ...additionalFilesSelection, [info.row.original.id]: checked });
          }}
        />
      ),
    }),
    columnHelper.accessor((row) => row.filename, {
      header: 'File Name',
      enableSorting: false,
      id: 'filename',
      cell: (info: any) => {
        const attachment = info.row.original;
        const extractDataJob = attachment.extractDataAIJob;
        const filename = info.getValue() || `Untitled ${attachment.contentType} ${info.row.index}`;
        return (
          <>
            <JobbableAttachmentJobSubscriber attachment={attachment} jobbable={review} />
            <FileNameContainer>
              {extractDataJob?.isErrored && (
                <Tooltip text='File failed to process and will not be included in the summary' position='bottom'>
                  <ErrorWarningIcon />
                </Tooltip>
              )}
              {extractDataJob?.isProcessing && (
                <SpinnerContainer>
                  <CircularProgress size={16} thickness={2} color={colors.primaryBlue} />
                </SpinnerContainer>
              )}
              <DocumentLink entry={attachment}>
                <BodySmallBold className='first-column' color={colors.primaryBlue}>
                  {filename}
                </BodySmallBold>
              </DocumentLink>
            </FileNameContainer>
          </>
        );
      },
    }),
    columnHelper.accessor(
      (row) => ({ userFullName: row.createdByUser?.fullName, groupName: row.createdByGroup?.name }),
      {
        id: 'createdByUser',
        enableSorting: false,
        header: 'Uploaded By',
        cell: (info) => (
          <div>
            {info.getValue().userFullName} {info.getValue().groupName && `at ${info.getValue().groupName}`}
          </div>
        ),
      }
    ),
    columnHelper.accessor((row) => row.createdAt, {
      header: 'Uploaded On',
      enableSorting: false,
      id: 'createdAt',
      cell: (info) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>{dateAtTime(info.getValue())}</div>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row, {
      header: '',
      enableSorting: false,
      id: 'delete',
      size: 44,
      cell: (info) => <RemoveButton onClick={() => removeFile(info.row.original)}>Remove</RemoveButton>,
    }),
  ];
}

const RemoveButton = styled(Label)`
  cursor: pointer;
  color: ${colors.accentRed};
`;

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// something about wrapping the spinner in a div
// makes it so that it doesn't get squished
// when the filename is long
const SpinnerContainer = styled.div``;
